import { useQuery } from "@tanstack/react-query";
import { ShieldAlert, Brain, Users, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LoadingSkeleton } from "@/components/states/LoadingSkeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface MemoryRow {
  id: string;
  content: string;
  memory_type: string;
  memory_layer: string;
  confidence: number | null;
  access_count: number;
  quarantined: boolean;
  quarantine_reason: string | null;
  last_accessed_at: string | null;
  created_at: string;
}

export default function MemoryAudit() {
  const { data: quarantined, isLoading: loadingQ } = useQuery({
    queryKey: ["mem-audit-quarantine"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("consolidated_memories")
        .select("id, content, memory_type, memory_layer, confidence, access_count, quarantined, quarantine_reason, last_accessed_at, created_at")
        .eq("quarantined", true)
        .order("updated_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as MemoryRow[];
    },
  });

  const { data: top, isLoading: loadingT } = useQuery({
    queryKey: ["mem-audit-top"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("consolidated_memories")
        .select("id, content, memory_type, memory_layer, confidence, access_count, quarantined, quarantine_reason, last_accessed_at, created_at")
        .eq("active", true)
        .eq("quarantined", false)
        .order("access_count", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as MemoryRow[];
    },
  });

  return (
    <div className="container mx-auto p-6 space-y-6">
      <header>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Brain className="h-6 w-6 text-primary" />
          Auditoria de Memória
        </h1>
        <p className="text-sm text-muted-foreground">
          Quarentena, memórias mais acessadas e indicadores de qualidade do sistema de memórias consolidadas.
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-muted-foreground">
              <ShieldAlert className="h-4 w-4" />
              Em quarentena
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{quarantined?.length ?? 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-muted-foreground">
              <Users className="h-4 w-4" />
              Top acessadas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{top?.length ?? 0}</p>
            <p className="text-xs text-muted-foreground">limit 50</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-muted-foreground">
              <Clock className="h-4 w-4" />
              Stale (não acessada 30d+)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">
              {top?.filter((m) => {
                if (!m.last_accessed_at) return true;
                return Date.now() - new Date(m.last_accessed_at).getTime() > 30 * 86400000;
              }).length ?? 0}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Memórias em quarentena</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingQ ? (
            <LoadingSkeleton />
          ) : !quarantined || quarantined.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhuma memória em quarentena.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Conteúdo</TableHead>
                  <TableHead>Camada</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead className="text-right">Acessos</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {quarantined.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell className="max-w-md truncate">{m.content}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{m.memory_layer}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{m.quarantine_reason ?? "—"}</TableCell>
                    <TableCell className="text-right">{m.access_count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Top 50 memórias acessadas</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingT ? (
            <LoadingSkeleton />
          ) : !top || top.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sem dados.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Conteúdo</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="text-right">Confiança</TableHead>
                  <TableHead className="text-right">Acessos</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {top.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell className="max-w-md truncate">{m.content}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{m.memory_type}</Badge>
                    </TableCell>
                    <TableCell className="text-right">{((m.confidence ?? 0) * 100).toFixed(0)}%</TableCell>
                    <TableCell className="text-right">{m.access_count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
